import * as React from "react"
import { type FieldPath, type FieldValues, type UseFormSetError } from "react-hook-form"

import { useTranslations } from "@/i18n"
import { resolveApiErrorMessage, type ErrorTranslator } from "@/shared/lib/api-errors/api-error"
import { SHARED_API_ERROR_NS } from "@/shared/lib/api-errors/api-error-codes"
import { applyApiFieldErrors } from "@/shared/lib/api-errors/form-api-errors"

/** A machine code is UPPER_SNAKE_CASE. Backend field errors are sometimes codes, often prose. */
const CODE_SHAPE = /^[A-Z][A-Z0-9_]+$/

/**
 * Translate a single backend field-error value: if it is a machine code, resolve it via the feature
 * then shared namespace; otherwise (prose, the common case) fall back to the generic translated
 * `errors.api.FIELD_INVALID`. NEVER echoes the raw backend string.
 */
function translateFieldMessage(rawMessage: string, error: unknown, t: ErrorTranslator, domain?: string): string {
  const trimmed = rawMessage.trim()
  if (CODE_SHAPE.test(trimmed)) {
    const keys = domain ? [`${domain}.errors.${trimmed}`, `${SHARED_API_ERROR_NS}.${trimmed}`] : [`${SHARED_API_ERROR_NS}.${trimmed}`]
    for (const key of keys) {
      const value = t(key)
      if (typeof value === "string" && value.length > 0 && value !== key) return value
    }
  }
  // Backend field-error values are markers ("Duplicate", "Invalid", "Required"), NOT user copy. Show the
  // error's specific, code-resolved message on the field (e.g. "A group with this name already exists.")
  // rather than a useless blanket "invalid value".
  return resolveApiErrorMessage(error, t, { domain })
}

type ApplyFieldErrorsOptions<TFieldValues extends FieldValues> = {
  fieldNameMap?: Partial<Record<string, FieldPath<TFieldValues> | null>>
}

/**
 * Translated replacement for the raw `applyApiFieldErrors`: maps backend `fieldErrors` onto RHF
 * field state with localized messages (forms.rules.md / ADR-0006 "fieldErrors map to fields").
 *
 *   const applyFieldErrors = useApplyApiFieldErrors<FormValues>("finance")
 *   catch (error) { applyFieldErrors(form.setError, error, { fieldNameMap }) }
 */
export function useApplyApiFieldErrors<TFieldValues extends FieldValues>(domain?: string) {
  const t = useTranslations()
  return React.useCallback(
    (setError: UseFormSetError<TFieldValues>, error: unknown, options?: ApplyFieldErrorsOptions<TFieldValues>) => {
      applyApiFieldErrors(setError, error, {
        fieldNameMap: options?.fieldNameMap,
        transformMessage: (message) => translateFieldMessage(message, error, t, domain),
      })
    },
    [t, domain],
  )
}

/**
 * Returns a memoized resolver for the form-level banner message (forms.rules.md "formErrors map to a
 * form banner"). Localized, never raw. Pass the feature error namespace as `domain`.
 */
export function useApiFormBannerMessage(domain?: string) {
  const t = useTranslations()
  return React.useCallback((error: unknown) => resolveApiErrorMessage(error, t, { domain }), [t, domain])
}
